import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { EducationalHub } from "../../../types/EducationalHub";
import { formatHubDate, getHubTitle, getUserLabel } from "./hubManagementUtils";
import s from "./HubManagement.module.css";

type HubListTableProps = {
  rows: EducationalHub[];
  logos: Record<string, string>;
  page: number;
  rowsPerPage: number;
  count: number;
  loader: boolean;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
};

type TablePaginationActionsProps = {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number,
  ) => void;
};

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#313131",
    color: "white",
    fontSize: 13,
    fontWeight: 700,
  },
  [`&.${tableCellClasses.body}`]: {
    color: "white",
    fontSize: 13,
    padding: "10px 14px",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#2E2E2E",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "#242424",
  },
  "td, th": {
    border: 0,
  },
}));

function TablePaginationActions({
  count,
  page,
  rowsPerPage,
  onPageChange,
}: TablePaginationActionsProps) {
  const theme = useTheme();
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={(event) => onPageChange(event, 0)}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? (
          <LastPageIcon color="success" />
        ) : (
          <FirstPageIcon color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={(event) => onPageChange(event, page - 1)}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight color="success" />
        ) : (
          <KeyboardArrowLeft color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={(event) => onPageChange(event, page + 1)}
        disabled={page >= lastPage}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft color="success" />
        ) : (
          <KeyboardArrowRight color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={(event) => onPageChange(event, lastPage)}
        disabled={page >= lastPage}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? (
          <FirstPageIcon color="success" />
        ) : (
          <LastPageIcon color="success" />
        )}
      </IconButton>
    </Box>
  );
}

const StatusBadge = ({
  label,
  value,
}: {
  label: string;
  value: boolean | undefined;
}) => {
  if (value === undefined) return null;
  return (
    <span className={`${s.statusPill} ${value ? s.statusPositive : s.statusMuted}`}>
      {value ? label : `Not ${label.toLowerCase()}`}
    </span>
  );
};

const HubListTable = ({
  rows,
  logos,
  page,
  rowsPerPage,
  count,
  loader,
  onPageChange,
  onRowsPerPageChange,
}: HubListTableProps) => {
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  return (
    <TableContainer
      component={Paper}
      className={s.tableContainer}
      sx={{
        width: "100%",
        backgroundColor: "transparent",
        borderRadius: "8px",
        maxHeight: "calc(100vh - 230px)",
      }}
    >
      <Table stickyHeader aria-label="educational hubs table">
        <TableHead>
          <TableRow>
            <StyledTableCell>Educational Hub</StyledTableCell>
            <StyledTableCell>Short ID</StyledTableCell>
            <StyledTableCell>Subdomain</StyledTableCell>
            <StyledTableCell>Owner</StyledTableCell>
            <StyledTableCell>Team</StyledTableCell>
            <StyledTableCell>Status</StyledTableCell>
            <StyledTableCell>Created</StyledTableCell>
            <StyledTableCell>Approved</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loader ? (
            <StyledTableRow>
              <StyledTableCell colSpan={8} align="center">
                <div className={s.tableState}>Loading Educational Hubs...</div>
              </StyledTableCell>
            </StyledTableRow>
          ) : rows.length === 0 ? (
            <StyledTableRow>
              <StyledTableCell colSpan={8} align="center">
                <div className={s.tableState}>No Educational Hubs found</div>
              </StyledTableCell>
            </StyledTableRow>
          ) : (
            rows.map((hub) => (
              <StyledTableRow key={hub._id}>
                <StyledTableCell>
                  <div className={s.hubIdentity}>
                    {logos[hub._id] ? (
                      <img
                        src={logos[hub._id]}
                        alt={getHubTitle(hub)}
                        className={s.hubLogo}
                      />
                    ) : (
                      <div className={s.hubLogoPlaceholder}>
                        {getHubTitle(hub).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span>{getHubTitle(hub)}</span>
                  </div>
                </StyledTableCell>
                <StyledTableCell>{hub.shortId || "-"}</StyledTableCell>
                <StyledTableCell>{hub.subdomain || "-"}</StyledTableCell>
                <StyledTableCell>{getUserLabel(hub.createdBy)}</StyledTableCell>
                <StyledTableCell>{hub.team?.length ?? 0}</StyledTableCell>
                <StyledTableCell>
                  <div className={s.statusList}>
                    <StatusBadge label="Approved" value={hub.isApproved} />
                    <StatusBadge
                      label="Requested"
                      value={hub.isApproveRequested}
                    />
                    <StatusBadge label="Active" value={hub.isActive} />
                    <StatusBadge label="Overdue" value={hub.paymentOverdue} />
                    <StatusBadge label="Deleted" value={hub.deleted} />
                  </div>
                </StyledTableCell>
                <StyledTableCell>{formatHubDate(hub.createdAt)}</StyledTableCell>
                <StyledTableCell>{formatHubDate(hub.approvedAt)}</StyledTableCell>
              </StyledTableRow>
            ))
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              colSpan={8}
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              labelRowsPerPage="Rows per page:"
              slotProps={{
                select: {
                  inputProps: {
                    "aria-label": "rows per page",
                  },
                  native: true,
                },
              }}
              onPageChange={(_, newPage) => onPageChange(newPage)}
              onRowsPerPageChange={handleRowsPerPageChange}
              ActionsComponent={TablePaginationActions}
              sx={{ border: "none", color: "white" }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default HubListTable;
